import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { dec, eur } from "@/components/excelVoce/format";

const sel = "h-8 w-full rounded-md border border-input bg-transparent px-1 text-xs";
const n = (v) => Number(v || 0);
export const newRip = (riga = "", risorsa = "") => ({ key: Math.random().toString(36).slice(2), riga_computo_id: riga, voce_analisi_risorsa_id: risorsa, quantita: "", ore: "", prezzo_unitario: "" });

export const totaliRip = (rips) => rips.reduce((t, r) => ({ q: t.q + n(r.quantita), o: t.o + n(r.ore) }), { q: 0, o: 0 });

const RisorsaSelect = ({ rigaId, tipo, value, onChange, cache, load }) => {
  useEffect(() => { if (rigaId && !cache[rigaId]) load(rigaId); }, [rigaId]);
  const list = (cache[rigaId] || []).filter((r) => r.tipo === tipo);
  return (
    <select className={sel} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={!rigaId}>
      <option value="">Fuori analisi / generica</option>
      {list.map((r) => <option key={r.id} value={r.id}>{r.codice || "—"} · {r.descrizione}</option>)}
    </select>
  );
};

export const RipartizioniEditor = ({ tipo, voci, rips, setRips, form }) => {
  const mdo = tipo === "MANODOPERA";
  const [cache, setCache] = useState({});
  const load = (id) => api.get(`/righe/${id}/registro-effettivo/risorse`).then((r) => setCache((c) => ({ ...c, [id]: r.data })));
  const upd = (k, patch) => setRips(rips.map((r) => (r.key === k ? { ...r, ...patch } : r)));
  const t = totaliRip(rips);
  const resQ = form.quantita === "" ? null : n(form.quantita) - t.q;
  const resO = form.ore === "" ? null : n(form.ore) - t.o;
  return (
    <div className="space-y-2" data-testid="ripartizioni-editor">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{tipo === "MATERIALE" ? "Utilizzo sulle voci" : "Ripartizione sulle voci"}</span>
        <Button type="button" size="sm" variant="outline" onClick={() => setRips([...rips, newRip()])} data-testid="rip-add-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Voce</Button>
      </div>
      {rips.map((r, i) => (
        <div key={r.key} className="grid grid-cols-12 gap-1.5 items-center border rounded p-2" data-testid={`rip-row-${i}`}>
          <select className={`${sel} col-span-12 sm:col-span-4`} value={r.riga_computo_id} onChange={(e) => upd(r.key, { riga_computo_id: e.target.value, voce_analisi_risorsa_id: "" })} data-testid={`rip-voce-${i}`}>
            <option value="">Scegli voce…</option>
            {voci.map((v) => <option key={v.id} value={v.id}>{v.num_ord} · {v.tariffa} · {v.descrizione.slice(0, 60)}</option>)}
          </select>
          <div className="col-span-12 sm:col-span-3"><RisorsaSelect rigaId={r.riga_computo_id} tipo={tipo} value={r.voce_analisi_risorsa_id} onChange={(v) => upd(r.key, { voce_analisi_risorsa_id: v })} cache={cache} load={load} /></div>
          <Input className="h-8 text-xs col-span-4 sm:col-span-2" type="number" step="any" min="0" placeholder={mdo ? "Q.tà eseguita" : "Quantità"} value={r.quantita} onChange={(e) => upd(r.key, { quantita: e.target.value })} data-testid={`rip-quantita-${i}`} />
          {mdo && <Input className="h-8 text-xs col-span-3 sm:col-span-1" type="number" step="any" min="0" placeholder="Ore" value={r.ore} onChange={(e) => upd(r.key, { ore: e.target.value })} data-testid={`rip-ore-${i}`} />}
          {mdo && <Input className="h-8 text-xs col-span-3 sm:col-span-1" type="number" step="any" min="0" placeholder="€/U.M." title="Prezzo a misura (vuoto = prezzo contrattuale)" value={r.prezzo_unitario} onChange={(e) => upd(r.key, { prezzo_unitario: e.target.value })} data-testid={`rip-prezzo-${i}`} />}
          <Button type="button" size="icon" variant="ghost" className="col-span-2 sm:col-span-1 h-8" onClick={() => setRips(rips.filter((x) => x.key !== r.key))} data-testid={`rip-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs" data-testid="rip-totali">
        <span>Attribuito: <b className="font-mono">{dec(t.q, 0, 4)}</b>{mdo && <> · <b className="font-mono">{dec(t.o, 0, 2)} h</b></>}</span>
        {resQ !== null && <span className={resQ < -0.0001 ? "text-rose-700 font-semibold" : "text-slate-600"} data-testid="rip-residuo-q">
          {resQ < -0.0001 ? "Supera la quantità disponibile" : tipo === "MATERIALE" ? `Giacenza (non utilizzato): ${dec(resQ, 0, 4)}` : `Non attribuito: ${dec(resQ, 0, 4)}`}
          {tipo !== "MANODOPERA" && resQ >= 0 && form.prezzo_unitario !== "" && ` · ${eur(resQ * n(form.prezzo_unitario))}`}</span>}
        {mdo && resO !== null && <span className={resO < -0.0001 ? "text-rose-700 font-semibold" : "text-slate-600"} data-testid="rip-residuo-ore">{resO < -0.0001 ? "Supera le ore registrate" : `Ore non attribuite: ${dec(resO, 0, 2)}`}</span>}
      </div>
    </div>
  );
};

export const ripEccede = (rips, form) => {
  const t = totaliRip(rips);
  return (form.quantita !== "" && t.q > n(form.quantita) + 0.0001) || (form.ore !== "" && t.o > n(form.ore) + 0.0001) || (form.ore === "" && t.o > 0);
};
